import React, { createContext, useContext, useState, useEffect } from 'react';
import AuthRequired from '../components/auth/AuthRequired';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for existing token in localStorage
    const existingToken = localStorage.getItem('cognito_token');
    const storedUserInfo = localStorage.getItem('user_info');
    
    if (existingToken) {
      setIsAuthenticated(true);
      
      // If we have stored user info, use it
      if (storedUserInfo) {
        try {
          const userInfo = JSON.parse(storedUserInfo);
          setUser(userInfo);
        } catch (error) {
          console.error('Error parsing stored user info:', error);
        }
      } else {
        // Fall back to parsing the token
        try {
          const payload = JSON.parse(atob(existingToken.split('.')[1]));
          console.log('Token payload:', payload);
          setUser({
            sub: payload.sub,
            email: payload.email,
            username: payload['cognito:username'] || payload.username
          });
        } catch (error) {
          console.error('Error parsing stored Cognito token:', error);
          localStorage.removeItem('cognito_token');
          setIsAuthenticated(false);
        }
      }
    }
    
    setLoading(false);
  }, []);

  const setToken = (accessToken, idToken) => {
    // Store access token as cognito_token for API authentication
    localStorage.setItem('cognito_token', accessToken);
    setIsAuthenticated(true);
    
    try {
      // Use ID token for user info if provided, otherwise fall back to access token
      const tokenToParse = idToken || accessToken;
      const payload = JSON.parse(atob(tokenToParse.split('.')[1]));
      
      setUser({
        sub: payload.sub,
        email: payload.email,
        username: payload['cognito:username'] || payload.username,
        email_verified: payload.email_verified,
        token_use: payload.token_use
      });
      
      // Store user info separately if needed
      if (idToken) {
        localStorage.setItem('user_info', JSON.stringify({
          sub: payload.sub,
          email: payload.email,
          username: payload['cognito:username'] || payload.username,
          email_verified: payload.email_verified
        }));
      }
    } catch (error) {
      console.error('Error parsing Cognito token:', error);
    }
  };

  const logout = async () => {
    try {
      // Extract tokens from localStorage before clearing
      const tokensToTerminate = [];
      
      // Extract token from GUAC_AUTH if it exists
      const guacAuth = localStorage.getItem('GUAC_AUTH');
      if (guacAuth) {
        try {
          const authData = JSON.parse(guacAuth);
          if (authData.auth_token) {
            tokensToTerminate.push(authData.auth_token);
          }
        } catch (e) {
          console.warn('Failed to parse GUAC_AUTH:', e);
        }
      }
      
      // Extract any other tokens that might be stored
      Object.keys(localStorage).forEach(key => {
        if (key.includes('token') || key.includes('auth')) {
          const value = localStorage.getItem(key);
          if (value && value.length > 20) { // Likely a token
            tokensToTerminate.push(value);
          }
        }
      });
      
      // Call the domain logout endpoint first to block entire Guacamole domain
      if (user && user.username) {
        const API_BASE = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
        
        // Try domain logout first (most aggressive - blocks entire domain)
        const domainLogoutResponse = await fetch(`${API_BASE}/remote-connections/guac-logout`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ username: user.username }),
        });
      }
    } catch (error) {
      console.error('Error during Guacamole logout:', error);
      // Continue with local logout even if Guacamole logout fails
    }
    
    // Clear all local authentication and session data
    localStorage.removeItem('cognito_token');
    localStorage.removeItem('user_info');
    localStorage.removeItem('GUAC_AUTH'); // Clear stored Guacamole auth
    
    // Also clear any other Guacamole-related data that might be stored
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('guac_') || key.startsWith('GUAC_') || key.includes('token') || key.includes('auth')) {
        localStorage.removeItem(key);
      }
    });
    
    // Clear session storage as well
    sessionStorage.clear();
    
    setIsAuthenticated(false);
    setUser(null);
    return <AuthRequired />;
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, user, logout, loading, setToken }}>
      {children}
    </AuthContext.Provider>
  );
};